import type {
  AssetProfile,
  AssetProfileSummary,
  ProfileResearch,
  SentimentAnalysis,
  ThemeAnalysis,
} from "@akem/shared";

const PROFILE_ALIAS_MAP: Record<
  string,
  { canonicalId: string; overrideTicker?: string; overrideName?: string }
> = {
  GOOG: {
    canonicalId: "GOOGL",
    overrideTicker: "GOOG",
    overrideName: "Alphabet Inc. (Class C)",
  },
};

export function getTopProfilesMock(): AssetProfileSummary[] {
  return [
    {
      id: "AAPL",
      ticker: "AAPL",
      name: "Apple Inc.",
      sector: "Technology",
      industry: "Consumer Electronics",
      logoUrl: "",
      researchCompleteness: 72,
      lastResearchUpdate: new Date().toISOString(),
    },
    {
      id: "MSFT",
      ticker: "MSFT",
      name: "Microsoft Corp.",
      sector: "Technology",
      industry: "Software",
      logoUrl: "",
      researchCompleteness: 68,
      lastResearchUpdate: new Date().toISOString(),
    },
    {
      id: "NVDA",
      ticker: "NVDA",
      name: "NVIDIA Corp.",
      sector: "Technology",
      industry: "Semiconductors",
      logoUrl: "",
      researchCompleteness: 75,
      lastResearchUpdate: new Date().toISOString(),
    },
    {
      id: "AMZN",
      ticker: "AMZN",
      name: "Amazon.com, Inc.",
      sector: "Consumer Discretionary",
      industry: "Internet & Direct Marketing Retail",
      logoUrl: "",
      researchCompleteness: 64,
      lastResearchUpdate: new Date().toISOString(),
    },
    {
      id: "GOOGL",
      ticker: "GOOGL",
      name: "Alphabet Inc. (Class A)",
      sector: "Communication Services",
      industry: "Interactive Media & Services",
      logoUrl: "",
      researchCompleteness: 66,
      lastResearchUpdate: new Date().toISOString(),
    },
  ];
}

export function getProfileMock(idOrTicker: string): AssetProfile {
  const top = getTopProfilesMock();
  const normalized = idOrTicker.trim().toUpperCase();
  const alias = PROFILE_ALIAS_MAP[normalized];
  const lookupKey = alias?.canonicalId ?? normalized;
  const baseSummary =
    top.find(
      (p) =>
        p.id.toUpperCase() === lookupKey ||
        p.ticker.toUpperCase() === lookupKey
    ) ?? {
      id: lookupKey,
      ticker: alias?.overrideTicker ?? normalized,
      name: alias?.overrideName ?? normalized,
      sector: "Unknown",
      industry: "Unknown",
      logoUrl: "",
      researchCompleteness: 50,
      lastResearchUpdate: new Date().toISOString(),
    };

  const base: AssetProfileSummary = {
    ...baseSummary,
    ticker: alias?.overrideTicker ?? baseSummary.ticker,
    name: alias?.overrideName ?? baseSummary.name,
  };

  const research: ProfileResearch = {
    suppliers: [
      {
        id: "TSMC",
        name: "TSMC",
        ticker: "TSM",
        type: "supplier",
        relationship: "Chip foundry",
        importance: "critical",
        confidence: 0.9,
        evidence: ["https://www.tsmc.com/"],
        lastVerified: new Date().toISOString(),
      },
      {
        id: "Foxconn",
        name: "Foxconn",
        type: "supplier",
        relationship: "Assembly",
        importance: "major",
        confidence: 0.8,
        evidence: ["https://www.foxconn.com/"],
        lastVerified: new Date().toISOString(),
      },
    ],
    customers: [
      {
        id: "VZ",
        name: "Verizon",
        ticker: "VZ",
        type: "customer",
        relationship: "Carrier channel",
        importance: "minor",
        confidence: 0.6,
        evidence: ["https://www.verizon.com/"],
        lastVerified: new Date().toISOString(),
      },
    ],
    competitors: [
      {
        id: "SSNLF",
        name: "Samsung",
        type: "competitor",
        relationship: "Smartphones",
        importance: "major",
        confidence: 0.75,
        evidence: ["https://www.samsung.com/"],
        lastVerified: new Date().toISOString(),
      },
    ],
    products: [],
    leadership: [],
    recentNews: [
      {
        title: `${base.name} announces new product`,
        summary: "New device announced with improved performance.",
        source: "Press",
        url: null,
        publishedAt: new Date().toISOString(),
        significance: 6,
      },
    ],
    earnings: [],
    sources: [],
    lastUpdated: new Date().toISOString(),
    completenessScores: { suppliers: 70, customers: 50, competitors: 60 },
  };

  const themes: ThemeAnalysis[] = [
    {
      id: "ai-edge",
      theme: "AI at the Edge",
      relevance: 0.7,
      sentiment: "positive",
      evidence: [],
      impactedAreas: ["devices"],
      timestamp: new Date().toISOString(),
    },
    {
      id: "supply-chain",
      theme: "Supply Chain Resilience",
      relevance: 0.6,
      sentiment: "neutral",
      evidence: [],
      impactedAreas: ["manufacturing"],
      timestamp: new Date().toISOString(),
    },
  ];

  const sentiment: SentimentAnalysis = {
    overall: "neutral",
    scores: { news: 0.1, analyst: 0.05, social: 0.0, technical: -0.02 },
    drivers: ["Product cycle", "Macro conditions"],
    lastUpdated: new Date().toISOString(),
  };

  return {
    id: base.id,
    ticker: base.ticker,
    name: base.name,
    sector: base.sector || undefined,
    industry: base.industry || undefined,
    marketCap: 2500000000000,
    description: `${base.name} is a leading company in ${base.industry}.`,
    logoUrl: base.logoUrl || undefined,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    lastResearchUpdate: base.lastResearchUpdate || undefined,
    researchCompleteness: base.researchCompleteness,
    research,
    themes,
    sentiment,
    defaultModuleId: "default-profile-module",
    customModules: [],
  };
}

export function getDefaultProfileModuleMock(profile: {
  id: string;
  ticker: string;
  name: string;
}) {
  return {
    id: "default-profile-module",
    name: "Company Overview",
    isDefault: true,
    profileId: profile.id,
    widgets: [
      {
        id: "profile-header",
        type: "profileHeader",
        position: { x: 50, y: 50 },
        size: { width: 800, height: 150 },
        config: { showLogo: true, showMetrics: ["marketCap", "pe", "volume"] },
        dataBinding: { source: "profile", path: "basic" },
      },
      {
        id: "market-data",
        type: "marketData",
        position: { x: 50, y: 220 },
        size: { width: 350, height: 400 },
        config: { symbols: [profile.ticker], showChart: true },
        dataBinding: { source: "profile", path: "ticker" },
      },
      {
        id: "relationship-graph",
        type: "relationshipGraph",
        position: { x: 420, y: 220 },
        size: { width: 430, height: 400 },
        config: {
          showTypes: ["suppliers", "customers", "competitors"],
          layout: "force-directed",
        },
        dataBinding: { source: "profile", path: "research" },
      },
      {
        id: "theme-analysis",
        type: "themeAnalysis",
        position: { x: 50, y: 640 },
        size: { width: 400, height: 300 },
        config: { maxThemes: 5, showSentiment: true },
        dataBinding: { source: "profile", path: "themes" },
      },
      {
        id: "news-sentiment",
        type: "newsSentiment",
        position: { x: 470, y: 640 },
        size: { width: 380, height: 300 },
        config: { timeframe: "7d", sources: ["all"] },
        dataBinding: { source: "profile", path: "research.recentNews" },
      },
      {
        id: "impact-explorer",
        type: "impactExplorer",
        position: { x: 1250, y: 220 },
        size: { width: 600, height: 550 },
        config: { title: "Impact Explorer", ticker: profile.ticker },
      },
    ],
    layout: { type: "grid", columns: 12, rowHeight: 50, margin: 20 },
    connections: [
      {
        source: { widgetId: "market-data", outputId: "selectedStock" },
        target: { widgetId: "relationship-graph", inputId: "focusEntity" },
      },
      {
        source: { widgetId: "market-data", outputId: "selectedStock" },
        target: { widgetId: "impact-explorer", inputId: "filterStock" },
      },
    ],
  };
}
